import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';

export interface UpcomingEventData {
  slug?: string;
  eventName?: string;
  sportCategory?: string;
  sportCategorySlug?: string;
  status?: string;
  participantStatus?: string;
  date: string; // ISO date or formatted
  startTime?: string;
  endTime?: string;
  location?: string;
  mapLink?: string;
  rawDate?: Date;
  rawEndDate?: Date;
}

interface AgendaTerdekatCardProps {
  event?: UpcomingEventData | null;
}

export const AgendaTerdekatCard: React.FC<AgendaTerdekatCardProps> = ({ event }) => {
  const navigate = useNavigate();
  const [isOngoing, setIsOngoing] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const startMs = event?.rawDate
    ? new Date(event.rawDate).getTime()
    : event?.date
    ? new Date(event.date).getTime()
    : 0;

  let endMs = event?.rawEndDate ? new Date(event.rawEndDate).getTime() : 0;

  if (!endMs && startMs > 0 && event?.endTime) {
    const startDateObj = new Date(startMs);
    const endStr = event.endTime.replace(/WIB|WITA|WIT/gi, '').replace('.', ':').trim();
    const parts = endStr.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h) && !isNaN(m)) {
        const endDateObj = new Date(startDateObj);
        endDateObj.setHours(h, m, 0, 0);
        endMs = endDateObj.getTime();
      }
    }
  }

  if (!endMs && startMs > 0) {
    endMs = startMs + 2 * 60 * 60 * 1000;
  }

  useEffect(() => {
    if (!startMs || isNaN(startMs)) return;

    const calc = () => {
      const now = new Date().getTime();

      if (now >= startMs && now <= endMs) {
        setIsOngoing(true);
        const distance = endMs - now;
        if (distance <= 0) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          return;
        }
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((distance / (1000 * 60)) % 60);
        const seconds = Math.floor((distance / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds });
      } else if (now < startMs) {
        setIsOngoing(false);
        const distance = startMs - now;
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((distance / (1000 * 60)) % 60);
        const seconds = Math.floor((distance / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setIsOngoing(false);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [startMs, endMs]);

  const pad = (n: number) => String(n).padStart(2, '0');

  // Return null if no event
  if (!event) {
    return null;
  }

  const getStatusLabel = (statusRaw?: string, participantStatusRaw?: string) => {
    if (isOngoing) {
      return 'Sedang Berlangsung';
    }
    const st = (participantStatusRaw || statusRaw || '').toLowerCase();
    if (st === 'confirmed' || st === 'terkonfirmasi' || st === 'active') {
      return 'Terkonfirmasi';
    }
    if (st === 'waiting_leave' || st === 'pending_leave') {
      return 'Menunggu Approval Leave';
    }
    if (st === 'pending' || st === 'pending_review' || st === 'waiting') {
      return 'Pending Review';
    }
    return statusRaw || 'Terkonfirmasi';
  };

  const categoryLabel = event.sportCategory || 'Futsal';
  const statusDisplay = getStatusLabel(event.status, event.participantStatus);

  const hasStatsFeature = (categorySlug?: string): boolean => {
    if (!categorySlug) return false;
    const slug = categorySlug.toLowerCase().trim();
    const supportedSlugs = [
      'sepak-bola',
      'futsal',
      'mini-soccer',
      'badminton',
      'padel',
    ];
    return supportedSlugs.some((item) => slug === item || slug.includes(item));
  };

  const showStatsBtn = hasStatsFeature(event.sportCategorySlug);

  return (
    <div className="rounded-3xl p-5 bg-gradient-to-br from-[#FF7A4D] via-[#FC480D] to-[#D83B08] text-white flex flex-col gap-4 shadow-lg shadow-orange-500/20">
      {/* Badges Header */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-white bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
          {categoryLabel}
        </span>
        {isOngoing ? (
          <span className="text-xs font-bold text-white bg-emerald-500/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5 animate-pulse shadow-xs">
            <span className="w-2 h-2 rounded-full bg-white animate-ping" />
            Sedang Berlangsung
          </span>
        ) : (
          <span className="text-xs font-semibold text-white bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full">
            {statusDisplay}
          </span>
        )}
      </div>

      {/* Event Title */}
      <h3 className="text-xl font-bold text-white leading-snug">
        {event.eventName}
      </h3>

      {/* Event Meta */}
      <div className="flex flex-col gap-1.5 text-xs text-white/95">
        <div className="flex items-center gap-2">
          <Icon icon="ph:calendar-dots" className="w-4 h-4 flex-shrink-0 text-white/90" />
          <span>{event.date} {event.startTime ? `· ${event.startTime}` : ''} {event.endTime ? `- ${event.endTime}` : ''}</span>
        </div>
        {event.location && (
          <div className="flex items-center gap-2">
            <Icon icon="ph:map-pin" className="w-4 h-4 flex-shrink-0 text-white/90" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>

      {/* Countdown / Ongoing Box */}
      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3 flex flex-col gap-1.5 text-center">
        {isOngoing && (
          <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-white uppercase tracking-wider">
            <Icon icon="ph:play-circle-bold" className="w-3.5 h-3.5 text-emerald-300 animate-spin" />
            <span>Sedang Berlangsung · Berakhir Dalam:</span>
          </div>
        )}
        <div className="grid grid-cols-7 items-center text-center">
          <div className="flex flex-col items-center">
            <span className="text-xl font-bold text-white leading-tight">{pad(timeLeft.days)}</span>
            <span className="text-[10px] text-white/80 font-medium">Hari</span>
          </div>
          <span className="text-sm font-semibold text-white/60">:</span>
          <div className="flex flex-col items-center">
            <span className="text-xl font-bold text-white leading-tight">{pad(timeLeft.hours)}</span>
            <span className="text-[10px] text-white/80 font-medium">Jam</span>
          </div>
          <span className="text-sm font-semibold text-white/60">:</span>
          <div className="flex flex-col items-center">
            <span className="text-xl font-bold text-white leading-tight">{pad(timeLeft.minutes)}</span>
            <span className="text-[10px] text-white/80 font-medium">Menit</span>
          </div>
          <span className="text-sm font-semibold text-white/60">:</span>
          <div className="flex flex-col items-center">
            <span className="text-xl font-bold text-white leading-tight">{pad(timeLeft.seconds)}</span>
            <span className="text-[10px] text-white/80 font-medium">Detik</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={`grid ${showStatsBtn ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5 pt-1`}>
        <button
          type="button"
          onClick={() => {
            if (event.slug) {
              navigate(`/event/${event.slug}`);
            } else {
              navigate('/event');
            }
          }}
          className="h-10 rounded-full bg-white text-[#FC480D] text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm hover:bg-white/95 active:scale-98 transition-all"
        >
          <Icon icon="ph:info" className="w-4 h-4" />
          <span>Lihat Detail</span>
        </button>

        {showStatsBtn && (
          <button
            type="button"
            onClick={() => {
              if (event?.slug) {
                navigate(`/event/${event.slug}/statistik`);
              } else {
                navigate('/event');
              }
            }}
            className="h-10 rounded-full bg-white/16 border border-white/60 text-white text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-white/25 active:scale-98 transition-all"
          >
            <Icon icon="ph:chart-bar" className="w-4 h-4" />
            <span>Statistik</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default AgendaTerdekatCard;
